/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useIntl } from 'react-intl';

import { ExcelForecastCreatePayload } from '@futrli/predict/forecast/dashboard/types';
import {
  ExcelForecastValidationFailureResponse,
  ExcelForecastValidationSuccessResponse
} from '@futrli-sdk/predict';
import { generateForecast, getExcelFileValidation } from '@futrli/predict/core/api/forecast';
import { getErrorMessage, Logger } from '@futrli/shared-services';
import { getExcelUploadPresignedUrl, getFileScanStatus } from '@futrli/predict/core/api/organisations';
import sleep from '@futrli/shared-services/api-client/utils/getters';
import { toastError } from '@futrli/ui-components';

import { getExcelValidationErrorMessage } from './utils';

export type StepType = 'ready' | 'upload' | 'scan' | 'validation' | 'create' | 'update';

const getObjectWithoutNullFields = (data: ExcelForecastCreatePayload): Partial<ExcelForecastCreatePayload> =>
  Object.fromEntries(Object.entries(data).filter(([_, value]) => value !== null));

export const useUploadForecast = (workspaceId: string, organisationId: string) => {
  const intl = useIntl();
  const [uploadFailed, setUploadFailed] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [step, setStep] = useState<StepType>('ready');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [createdForecastId, setCreatedForecastId] = useState<string | null>(null);

  const errorsDictionary = useMemo(
    () => ({
      FORECAST_SHEET_MISSING: intl.formatMessage({
        id: 'error.excel_forecast.forecast_sheet_missing',
        defaultMessage:
          "File uploaded must contain a sheet named 'P&L Budget or Forecast'. Excel file does not have a sheet named 'P&L Budget or Forecast'"
      }),
      ORGANISATION_NAME_DO_NOT_MATCH: intl.formatMessage({
        id: 'error.excel_forecast.organisation_name_do_not_match',
        defaultMessage: 'Company name does not match the organisation the forecast is being imported to'
      }),
      TYPE_MUST_BE_FORECAST: intl.formatMessage({
        id: 'error.excel_forecast.type_must_be_forecast',
        defaultMessage: "Import type in cell B1 must be 'P&L budget/forecast'. Import type is invalid"
      }),
      ASSUMPTION_NAME_IS_BLANK_BUT_RELATED_FIELDS_POPULATED: intl.formatMessage({
        id: 'error.excel_forecast.assumption_name_blank_related_fields_populated',
        defaultMessage: 'Prediction name is blank, but prediction data exists on this row'
      }),
      ASSUMPTION_NAME_TOO_LONG: intl.formatMessage({
        id: 'error.excel_forecast.assumption_name_to_long',
        defaultMessage: 'Prediction name is too long. Maximum is 90 characters'
      }),
      VAT_PERCENTAGE_OUT_OF_RANGE: intl.formatMessage({
        id: 'error.excel_forecast.vat_percentage_error',
        defaultMessage:
          'The VAT/GST rate in column D must be a number. The maximum value is 100 with up to 2 decimals. VAT/GST rate is invalid'
      }),
      VAT_PERCENTAGE_NOT_ROUNDED: intl.formatMessage({
        id: 'error.excel_forecast.vat_percentage_error',
        defaultMessage:
          'The VAT/GST rate in column D must be a number. The maximum value is 100 with up to 2 decimals. VAT/GST rate is invalid'
      }),
      VAT_PERCENTAGE_NOT_NUMBER: intl.formatMessage({
        id: 'error.excel_forecast.vat_percentage_error',
        defaultMessage:
          'The VAT/GST rate in column D must be a number. The maximum value is 100 with up to 2 decimals. VAT/GST rate is invalid'
      }),
      PAYMENT_TERMS_OUT_OF_RANGE: intl.formatMessage({
        id: 'error.excel_forecast.payment_terms_error',
        defaultMessage:
          'Cash flow treatment should be either 0 or blank for same day payment. To set payment days, the number of days need to be whole numbers, maximum 999. Cash flow value is invalid'
      }),
      PAYMENT_TERMS_NOT_WHOLE_NUMBER: intl.formatMessage({
        id: 'error.excel_forecast.payment_terms_error',
        defaultMessage:
          'Cash flow treatment should be either 0 or blank for same day payment. To set payment days, the number of days need to be whole numbers, maximum 999. Cash flow value is invalid'
      }),
      PAYMENT_TERMS_NOT_NUMBER: intl.formatMessage({
        id: 'error.excel_forecast.payment_terms_error',
        defaultMessage:
          'Cash flow treatment should be either 0 or blank for same day payment. To set payment days, the number of days need to be whole numbers, maximum 999. Cash flow value is invalid'
      }),
      AMOUNT_OUT_OF_RANGE: intl.formatMessage({
        id: 'error.excel_forecast.amount_error',
        defaultMessage:
          'Prediction must be in the range -9,999,999,999 to +9,999,999,999 and no more than 2 decimal places. Prediction value is invalid'
      }),
      AMOUNT_NOT_NUMBER: intl.formatMessage({
        id: 'error.excel_forecast.amount_error',
        defaultMessage:
          'Prediction must be in the range -9,999,999,999 to +9,999,999,999 and no more than 2 decimal places. Prediction value is invalid'
      }),
      AMOUNT_NOT_ROUNDED: intl.formatMessage({
        id: 'error.excel_forecast.amount_error',
        defaultMessage:
          'Prediction must be in the range -9,999,999,999 to +9,999,999,999 and no more than 2 decimal places. Prediction value is invalid'
      }),
      INVALID_ACCOUNT_CATEGORY: intl.formatMessage({
        id: 'error.excel_forecast.invalid_account_category',
        defaultMessage: 'All accounts must be in the latest Chart of Accounts. Invalid Category or Account name'
      }),
      INVALID_ACCOUNT_CATEGORY_NAME_COMBINATION: intl.formatMessage({
        id: 'error.excel_forecast.invalid_account_category_name_combination',
        defaultMessage: 'All accounts must be in the latest Chart of Accounts. Account is in the incorrect Category'
      }),
      ASSUMPTION_NAME_NOT_UNIQUE: intl.formatMessage({
        id: 'error.excel_forecast.assumption_name_not_unique',
        defaultMessage: 'Predictions names must be unique. Prediction name is not unique'
      }),
      INVOICE_DATE_NOT_VALID: intl.formatMessage({
        id: 'error.excel_forecast.invoice_date_not_valid',
        defaultMessage:
          'Valid dates in column F are 1-31, with blank defaulting to the 1st of the month. Date is invalid'
      }),
      DATE_FORMAT_INCORRECT: intl.formatMessage({
        id: 'error.excel.driver_date_format_incorrect',
        defaultMessage:
          'Row 5 should only contain dates in MMM-YY format where there are prediction values. Value is not a date'
      }),
      DATE_ROW_HAD_BLANKS: intl.formatMessage({
        id: 'error.excel.date_row_had_blanks',
        defaultMessage:
          'Row 5 should only contain dates in MMM-YY format where there are prediction values. Date is blank'
      }),
      NOT_CONSECUTIVE_DATES: intl.formatMessage({
        id: 'error.excel.not_consecutive_dates',
        defaultMessage: 'Dates in row 5 must be consecutive. Date is not consecutive'
      }),
      INVALID_FILE: intl.formatMessage({
        id: 'error.invalid_file',
        defaultMessage: 'Invalid file'
      })
    }),
    [intl]
  );

  const warningsDictionary = useMemo(
    () => ({
      CONTAINS_HISTORICAL_PERIODS: intl.formatMessage({
        id: 'warning.file_contains_historical_periods',
        defaultMessage:
          'Warning: File contains periods not covered in the forecast date range, so those periods will not display.'
      })
    }),
    [intl]
  );

  const sheetName = useMemo(
    () => ({
      FORECAST: intl.formatMessage({
        id: 'financial_fields.forecast_sheet_name',
        defaultMessage: 'P&L Budget or Forecast'
      })
    }),
    [intl]
  );

  const handleError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      Logger.error(e as string);
      let errorMessage = getErrorMessage(e);
      if (typeof errorMessage === 'string' && errorMessage.toLocaleLowerCase() === 'network error') {
        errorMessage = intl.formatMessage({
          id: 'error.failed_upload_file_open_other_app',
          defaultMessage:
            'Failed to upload the file. Please ensure the file is not open in another application, then try again.'
        });
      }
      toastError(getErrorMessage(errorMessage));
      setUploadFailed(true);
    },
    [intl]
  );

  const uploadScanValidateExcelFile = useCallback(
    async (selectedFile: File, forecast_id: string | null, forecast_from_date: string) => {
      setErrorMessage('');
      setWarningMessage('');
      if (!selectedFile) {
        toastError(intl.formatMessage({ id: 'common.please_select_file', defaultMessage: 'Please select a file' }));
      }
      setStep('upload');
      setIsInProgress(true);
      setUploadFailed(false);
      setUploadSuccess(false);
      const res = await getExcelUploadPresignedUrl(workspaceId, selectedFile.name, 'forecast');
      if (res.excel_file_id && res.upload_url?.url) {
        const formData = new FormData();
        Object.entries(res.upload_url.fields).forEach(([field, value]) => {
          formData.append(field, value as string);
        });
        formData.append('Content-Type', selectedFile.type);
        formData.append('file', selectedFile);
        await axios.post(res.upload_url.url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        // scan status
        setStep('scan');
        let scanStatus = { scan_status: 'pending' };
        while (scanStatus.scan_status === 'pending') {
          scanStatus = await getFileScanStatus(workspaceId, res.excel_file_id);
          await sleep(500);
        }
        if (scanStatus.scan_status !== 'clean') {
          setErrorMessage(
            intl.formatMessage({
              id: 'error.scan_error_description',
              defaultMessage: 'We found a problem when scanning your file. Please check the file and try again.'
            })
          );
          throw new Error(
            intl.formatMessage({
              id: 'error.scan_error',
              defaultMessage: 'File scan failed.'
            })
          );
        }
        setStep('validation');
        const validationStatus = await getExcelFileValidation(
          workspaceId,
          organisationId,
          res.excel_file_id,
          forecast_id,
          forecast_from_date
        );
        if ((validationStatus as ExcelForecastValidationSuccessResponse)?.warning_code) {
          const warning = validationStatus as ExcelForecastValidationSuccessResponse;
          setWarningMessage(warningsDictionary[warning.warning_code]);
        }
        if ((validationStatus as ExcelForecastValidationFailureResponse)?.reason_code) {
          const error = validationStatus as ExcelForecastValidationFailureResponse;
          const reasonMessage = errorsDictionary[error.reason_code];

          const errorString = getExcelValidationErrorMessage(intl, reasonMessage, sheetName, error.sheet, error.cell);

          setErrorMessage(errorString);
          throw new Error(
            intl.formatMessage({
              id: 'error.validation_error',
              defaultMessage: 'File validation failed.'
            })
          );
        }
      }
      return res.excel_file_id;
    },
    [workspaceId, intl, organisationId, errorsDictionary, sheetName, warningsDictionary]
  );

  const createForecastFromExcelFile = useCallback(
    async (selectedFile: File, data: ExcelForecastCreatePayload) => {
      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile, null, data.forecast_from_date);
        setStep('create');
        const createdForecast = await generateForecast(
          workspaceId,
          organisationId,
          fileId,
          getObjectWithoutNullFields(data)
        );
        setUploadSuccess(true);
        setCreatedForecastId(createdForecast.forecast_id);
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [handleError, organisationId, uploadScanValidateExcelFile, workspaceId]
  );

  const updateForecastFromExcelFile = useCallback(
    async (selectedFile: File, data: ExcelForecastCreatePayload) => {
      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile, data.forecast_id, data.forecast_from_date);
        setStep('update');
        await generateForecast(workspaceId, organisationId, fileId, getObjectWithoutNullFields(data));
        setUploadSuccess(true);
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [handleError, organisationId, uploadScanValidateExcelFile, workspaceId]
  );

  return {
    createForecastFromExcelFile,
    updateForecastFromExcelFile,
    createdForecastId,
    uploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    warningMessage,
    setWarningMessage
  };
};
